import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Settings, 
  Image, 
  FileText, 
  Mail, 
  Share2, 
  LogOut, 
  Save,
  Inbox,
  Trash2,
  Check,
  Loader2,
  Upload,
  Key,
  Eye,
  EyeOff,
  X,
  Pickaxe,
  Sparkles
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tabs = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'content', label: 'Content', icon: FileText },
  { id: 'images', label: 'Images', icon: Image },
  { id: 'contact', label: 'Contact Info', icon: Mail },
  { id: 'social', label: 'Social Media', icon: Share2 },
  { id: 'inquiries', label: 'Inquiries', icon: Inbox },
  { id: 'security', label: 'Security', icon: Key },
  { id: 'ai', label: "Kishen's AI", icon: Sparkles },
];

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyAndFetch();
  }, [token, navigate]);

  const verifyAndFetch = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchSettings();
      await fetchInquiries();
    } catch (err) {
      localStorage.removeItem('adminToken');
      navigate('/admin');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data);
    } catch (err) {
      console.error('Failed to fetch inquiries:', err);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
      setHasChanges(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(prev => prev.filter(i => i.id !== id));
      toast.success('Inquiry deleted');
    } catch (err) {
      toast.error('Failed to delete inquiry');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="animate-spin text-cyan-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
              <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                ZenithForge
              </span>
            </div>
            <div className="flex items-center gap-4">
              {hasChanges && (
                <Button
                  onClick={saveSettings}
                  disabled={isSaving}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                  data-testid="save-settings-button"
                >
                  {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                  Save Changes
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="logout-button"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            {/* Crypto Dashboard Link */}
            <Link
              to="/crypto"
              className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-purple-400 hover:from-purple-600/30 hover:to-cyan-600/30 transition-all"
            >
              <Pickaxe size={20} />
              <span className="font-medium">CryptoMiner App</span>
            </Link>
            
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'ai') { navigate('/admin/ai'); return; }
                    setActiveTab(tab.id);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={20} />
                  <span className="font-medium">{tab.label}</span>
                  {tab.id === 'inquiries' && inquiries.length > 0 && (
                    <span className="ml-auto bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {inquiries.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-slate-800 rounded-2xl p-8 border border-slate-700"
            >
              {activeTab === 'general' && settings && (
                <GeneralSettings settings={settings} onChange={handleChange} />
              )}
              {activeTab === 'content' && settings && (
                <ContentSettings settings={settings} onChange={handleChange} />
              )}
              {activeTab === 'images' && settings && (
                <ImageSettings settings={settings} onChange={handleChange} token={token} />
              )}
              {activeTab === 'contact' && settings && (
                <ContactSettings settings={settings} onChange={handleChange} />
              )}
              {activeTab === 'social' && settings && (
                <SocialSettings settings={settings} onChange={handleChange} />
              )}
              {activeTab === 'inquiries' && (
                <InquiriesPanel inquiries={inquiries} onDelete={deleteInquiry} />
              )}
              {activeTab === 'security' && (
                <SecuritySettings token={token} />
              )}
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};

// Settings Panels
const SettingField = ({ label, value, onChange, type = 'text', placeholder, multiline }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
    {multiline ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
      />
    ) : (
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-500"
      />
    )}
  </div>
);

const GeneralSettings = ({ settings, onChange }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">General Settings</h2>
    <div className="grid md:grid-cols-2 gap-6">
      <SettingField
        label="Company Name"
        value={settings.company_name}
        onChange={(v) => onChange('company_name', v)}
        placeholder="ZenithForge Technology"
      />
      <SettingField
        label="Tagline"
        value={settings.tagline}
        onChange={(v) => onChange('tagline', v)}
        placeholder="Your Trusted IT Partner"
      />
    </div>
    <SettingField
      label="Hero Title"
      value={settings.hero_title}
      onChange={(v) => onChange('hero_title', v)}
      placeholder="Empowering Your Business with"
    />
    <SettingField
      label="Hero Subtitle"
      value={settings.hero_subtitle}
      onChange={(v) => onChange('hero_subtitle', v)}
      placeholder="Main description text"
      multiline
    />
  </div>
);

const ContentSettings = ({ settings, onChange }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">About Page Content</h2>
    <SettingField
      label="About Introduction"
      value={settings.about_intro}
      onChange={(v) => onChange('about_intro', v)}
      multiline
    />
    <SettingField
      label="Vision Statement"
      value={settings.vision}
      onChange={(v) => onChange('vision', v)}
      multiline
    />
    <h3 className="text-lg font-semibold text-white mb-4 mt-8">Mission Points</h3>
    <SettingField label="Mission 1" value={settings.mission_1} onChange={(v) => onChange('mission_1', v)} />
    <SettingField label="Mission 2" value={settings.mission_2} onChange={(v) => onChange('mission_2', v)} />
    <SettingField label="Mission 3" value={settings.mission_3} onChange={(v) => onChange('mission_3', v)} />
    <SettingField label="Mission 4" value={settings.mission_4} onChange={(v) => onChange('mission_4', v)} />
    <h3 className="text-lg font-semibold text-white mb-4 mt-8">Founder Information</h3>
    <div className="grid md:grid-cols-2 gap-6">
      <SettingField label="Founder Name" value={settings.founder_name} onChange={(v) => onChange('founder_name', v)} />
      <SettingField label="Founder Title" value={settings.founder_title} onChange={(v) => onChange('founder_title', v)} />
    </div>
    <SettingField label="Founder Quote" value={settings.founder_quote} onChange={(v) => onChange('founder_quote', v)} multiline />
  </div>
);

// Image Upload Component
const ImageUploadField = ({ label, value, onChange, token, fieldName }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value);

  useEffect(() => {
    setPreviewUrl(value);
  }, [value]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, WebP, or SVG.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const fullUrl = `${BACKEND_URL}${response.data.url}`;
      setPreviewUrl(fullUrl);
      onChange(fullUrl);
      toast.success('Image uploaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
      
      <div className="flex gap-4 items-start">
        {/* Preview */}
        <div className="w-32 h-32 bg-slate-900 rounded-xl border border-slate-600 overflow-hidden flex-shrink-0">
          {previewUrl ? (
            <img 
              src={previewUrl} 
              alt={label} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500">
              <Image size={32} />
            </div>
          )}
        </div>

        {/* Upload area */}
        <div className="flex-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full h-24 border-2 border-dashed border-slate-600 rounded-xl hover:border-cyan-500 transition-colors flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-cyan-400"
          >
            {isUploading ? (
              <Loader2 className="animate-spin" size={24} />
            ) : (
              <>
                <Upload size={24} />
                <span className="text-sm">Click to upload or drag & drop</span>
              </>
            )}
          </button>
          
          <p className="text-xs text-slate-500 mt-2">JPG, PNG, GIF, WebP, SVG • Max 5MB</p>
          
          {/* URL Input as fallback */}
          <div className="mt-3">
            <Input
              type="text"
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
                setPreviewUrl(e.target.value);
              }}
              placeholder="Or paste image URL..."
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-cyan-500 text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageSettings = ({ settings, onChange, token }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">Image Management</h2>
    <p className="text-slate-400 mb-6">Upload images directly or paste URLs from Unsplash, Pexels, or your own hosted images.</p>
    
    <ImageUploadField 
      label="Company Logo" 
      value={settings.logo_url} 
      onChange={(v) => onChange('logo_url', v)} 
      token={token}
      fieldName="logo"
    />
    
    <ImageUploadField 
      label="Hero Section Image" 
      value={settings.hero_image_url} 
      onChange={(v) => onChange('hero_image_url', v)} 
      token={token}
      fieldName="hero"
    />
    
    <ImageUploadField 
      label="About Page Image" 
      value={settings.about_image_url} 
      onChange={(v) => onChange('about_image_url', v)} 
      token={token}
      fieldName="about"
    />
    
    <h3 className="text-lg font-semibold text-white mb-4 mt-8">Services Page Images</h3>
    
    <ImageUploadField 
      label="Managed IT Services Image" 
      value={settings.services_image_1} 
      onChange={(v) => onChange('services_image_1', v)} 
      token={token}
      fieldName="service1"
    />
    
    <ImageUploadField 
      label="IT Consulting Image" 
      value={settings.services_image_2} 
      onChange={(v) => onChange('services_image_2', v)} 
      token={token}
      fieldName="service2"
    />
    
    <ImageUploadField 
      label="Project Management Image" 
      value={settings.services_image_3} 
      onChange={(v) => onChange('services_image_3', v)} 
      token={token}
      fieldName="service3"
    />
  </div>
);

const ContactSettings = ({ settings, onChange }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">Contact Information</h2>
    <div className="grid md:grid-cols-2 gap-6">
      <SettingField label="Email Address" value={settings.email} onChange={(v) => onChange('email', v)} type="email" />
      <SettingField label="Phone Number" value={settings.phone} onChange={(v) => onChange('phone', v)} />
      <SettingField label="Location" value={settings.location} onChange={(v) => onChange('location', v)} />
      <SettingField label="Business Hours" value={settings.business_hours} onChange={(v) => onChange('business_hours', v)} />
      <SettingField label="WhatsApp Number (without +)" value={settings.whatsapp_number} onChange={(v) => onChange('whatsapp_number', v)} placeholder="27720350706" />
    </div>
  </div>
);

const SocialSettings = ({ settings, onChange }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">Social Media Links</h2>
    <p className="text-slate-400 mb-6">Leave empty to hide the social icon.</p>
    <SettingField label="LinkedIn URL" value={settings.linkedin_url} onChange={(v) => onChange('linkedin_url', v)} placeholder="https://linkedin.com/company/..." />
    <SettingField label="Twitter/X URL" value={settings.twitter_url} onChange={(v) => onChange('twitter_url', v)} placeholder="https://twitter.com/..." />
    <SettingField label="Facebook URL" value={settings.facebook_url} onChange={(v) => onChange('facebook_url', v)} placeholder="https://facebook.com/..." />
    <SettingField label="Instagram URL" value={settings.instagram_url} onChange={(v) => onChange('instagram_url', v)} placeholder="https://instagram.com/..." />
  </div>
);

const InquiriesPanel = ({ inquiries, onDelete }) => (
  <div>
    <h2 className="text-xl font-bold text-white mb-6">Contact Inquiries</h2>
    {inquiries.length === 0 ? (
      <div className="text-center py-12 text-slate-400">
        <Inbox size={48} className="mx-auto mb-4 opacity-50" />
        <p>No inquiries yet</p>
      </div>
    ) : (
      <div className="space-y-4">
        {inquiries.map((inquiry) => (
          <motion.div
            key={inquiry.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 rounded-xl p-6 border border-slate-700"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{inquiry.name}</h3>
                <p className="text-cyan-400">{inquiry.email}</p>
                {inquiry.phone && <p className="text-slate-400 text-sm">{inquiry.phone}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {new Date(inquiry.created_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => onDelete(inquiry.id)}
                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            {inquiry.company && (
              <p className="text-slate-400 text-sm mb-2">Company: {inquiry.company}</p>
            )}
            {inquiry.service_interest && (
              <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm mb-3">
                {inquiry.service_interest}
              </span>
            )}
            <p className="text-slate-300 mt-2">{inquiry.message}</p>
          </motion.div>
        ))}
      </div>
    )}
  </div>
);

const SecuritySettings = ({ token }) => {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isChanging, setIsChanging] = useState(false);

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setIsChanging(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">Security Settings</h2>
      
      <div className="max-w-md">
        <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Current Password</label>
            <div className="relative">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white pr-10"
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white pr-10"
                data-testid="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Confirm New Password</label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white pr-10"
                data-testid="confirm-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={isChanging}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white mt-4"
            data-testid="change-password-button"
          >
            {isChanging ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Changing...
              </>
            ) : (
              <>
                <Key className="mr-2" size={16} />
                Change Password
              </>
            )}
          </Button>
        </div>

        <div className="mt-8 p-4 bg-slate-900 rounded-xl border border-slate-700">
          <h4 className="font-medium text-white mb-2">Password Requirements</h4>
          <ul className="text-sm text-slate-400 space-y-1">
            <li>• At least 6 characters long</li>
            <li>• Mix of letters and numbers recommended</li>
            <li>• Avoid using common words or phrases</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
